import { AlertCircle } from 'lucide-react';
import { Button } from './button';

interface ErrorStateProps {
  message: string;
  hint?: string;
  onRetry?: () => void;
  isLoading?: boolean;
  /** Defaults to "Something went wrong" — generic, not feature-specific. */
  title?: string;
}

/**
 * Generic error state for any screen. Screens that need specific copy
 * (e.g. the investigator's "Investigation Failed") pass it via `title`.
 */
export function ErrorState({
  message,
  hint,
  onRetry,
  isLoading = false,
  title = 'Something went wrong',
}: ErrorStateProps) {
  return (
    <div className="flex flex-col items-center justify-center h-full min-h-100 text-center px-8">
      <div className="w-16 h-16 rounded-full bg-destructive/20 border border-destructive/40 flex items-center justify-center mb-4">
        <AlertCircle className="w-7 h-7 text-destructive" aria-hidden />
      </div>
      <h2 className="text-lg font-medium text-destructive mb-2">{title}</h2>
      <p className="text-sm text-muted-foreground max-w-md leading-relaxed mb-4">{message}</p>
      {hint ? (
        <p className="text-xs text-muted-foreground max-w-md leading-relaxed mb-4 border-t border-border pt-3">
          {hint}
        </p>
      ) : null}
      {onRetry ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onRetry}
          loading={isLoading}
        >
          {isLoading ? 'Retrying…' : 'Try Again'}
        </Button>
      ) : null}
    </div>
  );
}
